import { motion } from 'framer-motion';
import { format, addWeeks, subWeeks, startOfWeek, endOfWeek, addDays, isSameDay, isBefore, startOfToday, setHours, setMinutes, getDay } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, MessageCircle, X, User, Mail, Phone, Instagram, Bookmark, Calendar, Clock } from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import React from 'react';
import { useBookingSystem, type GoogleCalendarEvent } from '@/contexts/BookingSystemContext';
import type { WeekDay, DaySettings } from '@/types/booking';
import { collection, query, where, getDocs, doc as firestoreDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { UserData } from '@/types/models';
import { Dialog, DialogPortal } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';

const HOURS = Array.from({ length: 14 }, (_, i) => i + 8); // 8 to 19
const MINUTES = [0, 15, 30, 45]; // 15-minute intervals

// Generate all time slots (hour + minute combinations)
const TIME_SLOTS = HOURS.flatMap(hour => 
  MINUTES.map(minute => ({ hour, minute }))
);

// Map date-fns day number (0=Sunday) to WeekDay type
const DAY_MAP: Record<number, WeekDay> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday'
};

interface Booking {
  id: string;
  artistId: string;
  chatId: string;
  clientUsername: string;
  scheduledTimestamp: Timestamp;
  createdAt: Timestamp;
  customDesignStatus?: string;
  shortSummary?: string;
  type: 'custom' | 'flash';
  userId: string;
  estimatedDuration: number; // in hours
  clientInstagram?: string;
  clientEmail?: string;
  clientPhoneNumber?: string;
}

// Type for iOS calendar event display (derived from IosCalendarOverride)
interface IosCalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
}

export function BookingSystemCalendar() {
  const navigate = useNavigate();
  const { availability, artistId, googleCalendar, iosCalendar } = useBookingSystem();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [now, setNow] = useState(new Date());
  const [slideDirection, setSlideDirection] = useState(0); // -1 for left, 1 for right
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState<GoogleCalendarEvent | null>(null);
  const [selectedIosCalendarEvent, setSelectedIosCalendarEvent] = useState<IosCalendarEvent | null>(null);
  const [weekCalendarEvents, setWeekCalendarEvents] = useState<GoogleCalendarEvent[]>([]);
  const [weekIosCalendarEvents, setWeekIosCalendarEvents] = useState<IosCalendarEvent[]>([]);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Fetch bookings for the current week
  useEffect(() => {
    const fetchBookings = async () => {
      if (!artistId) return;

      try {
        console.log('Querying bookings for artistId:', artistId);
        
        // Get start of week at 00:00:00 and end of week at 23:59:59
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
        weekEnd.setHours(23, 59, 59, 999);

        console.log('Query range:', {
          weekStart: weekStart.toISOString(),
          weekEnd: weekEnd.toISOString()
        });

        const bookingsRef = collection(db, 'bookings');
        const bookingQuery = query(
          bookingsRef,
          where('artistId', '==', artistId)
          // Temporarily remove timestamp filters to see if we get any bookings at all
        );

        const querySnapshot = await getDocs(bookingQuery);
        console.log('Found bookings:', querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));
        
        // Fetch user data for each booking to get client's Instagram and phone
        const weekBookings = await Promise.all(querySnapshot.docs.map(async (doc) => {
          const data = doc.data() as any;
          const estimatedDuration = (data.estimatedDuration as number | undefined) ?? (data.estimatedTime as number | undefined) ?? 1;
          
          // Extract clientUsername from clientData.Username or fall back to direct field for backward compatibility
          const clientUsername = data.clientData?.Username || data.clientUsername || 'Unknown'
          
          // Fetch the actual user document to get their Instagram and phone number
          let clientInstagram: string | undefined;
          let clientEmail: string | undefined;
          let clientPhoneNumber: string | undefined;
          
          if (data.userId) {
            try {
              const userDocRef = firestoreDoc(db, 'users', data.userId);
              const userDoc = await getDoc(userDocRef);
              
              if (userDoc.exists()) {
                const userData = userDoc.data() as UserData;
                clientInstagram = userData.instagram;
                clientEmail = userData.email;
                clientPhoneNumber = userData.phoneNumber;
                console.log('[BookingSystemCalendar] Fetched user data for', data.userId, ':', {
                  instagram: clientInstagram,
                  email: clientEmail,
                  phoneNumber: clientPhoneNumber
                });
              }
            } catch (userError) {
              console.error('[BookingSystemCalendar] Error fetching user data:', userError);
            }
          }
          
          // Fallback to clientData if user document fetch failed or fields are missing
          if (!clientEmail) {
            clientEmail = data.clientData?.email || data.clientData?.Email || data.clientEmail;
          }
          if (!clientInstagram) {
            clientInstagram = data.clientData?.username || data.clientData?.InstagramHandle || data.clientData?.instagram || data.clientInstagram;
          }
          if (!clientPhoneNumber) {
            clientPhoneNumber = data.clientData?.['Phone Number'] || data.clientData?.PhoneNumber || data.clientData?.phoneNumber || data.clientData?.phone || data.clientPhoneNumber;
          }
          
          const booking: Booking = {
            id: doc.id,
            artistId: data.artistId,
            chatId: data.chatId,
            clientUsername: clientUsername,
            scheduledTimestamp: data.scheduledTimestamp,
            createdAt: data.createdAt,
            customDesignStatus: data.customDesignStatus,
            shortSummary: data.shortSummary,
            type: data.type,
            userId: data.userId,
            estimatedDuration,
            clientInstagram,
            clientEmail,
            clientPhoneNumber,
          };
          return booking;
        }));

        setBookings(weekBookings);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      }
    };

    fetchBookings();
  }, [artistId, currentDate]);

  // Filter Google Calendar events for the current week
  useEffect(() => {
    if (!googleCalendar.events) {
      setWeekCalendarEvents([]);
      return;
    }

    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });

    const filteredEvents = googleCalendar.events.filter(event => {
      const eventStart = event.start;
      return eventStart >= weekStart && eventStart <= weekEnd;
    });

    console.log('[BookingSystemCalendar] Google Calendar events for week:', {
      weekStart: weekStart.toISOString(),
      weekEnd: weekEnd.toISOString(),
      totalEvents: googleCalendar.events.length,
      filteredEvents: filteredEvents.length,
      events: filteredEvents.map(e => ({ summary: e.summary, start: e.start.toISOString() }))
    });

    setWeekCalendarEvents(filteredEvents);
  }, [currentDate, googleCalendar.events]);

  // Filter iOS Calendar events for the current week
  useEffect(() => {
    if (!availability?.iosOverrides || availability.iosOverrides.length === 0) {
      setWeekIosCalendarEvents([]);
      return;
    }

    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    weekEnd.setHours(23, 59, 59, 999);

    const filteredEvents: IosCalendarEvent[] = availability.iosOverrides
      .filter(override => {
        const eventStart = new Date(override.startDate);
        const eventEnd = new Date(override.endDate);
        // Include events that overlap with the week (even if they start before or end after)
        return eventStart <= weekEnd && eventEnd >= weekStart;
      })
      .map(override => ({
        id: override.eventId,
        title: override.eventTitle,
        start: new Date(override.startDate),
        end: new Date(override.endDate),
      }));

    console.log('[BookingSystemCalendar] iOS Calendar events for week:', {
      weekStart: weekStart.toISOString(),
      weekEnd: weekEnd.toISOString(),
      totalOverrides: availability.iosOverrides.length,
      filteredEvents: filteredEvents.length,
      rawOverrides: availability.iosOverrides.map(o => ({
        title: o.eventTitle,
        startDate: o.startDate,
        endDate: o.endDate,
        startISO: new Date(o.startDate).toISOString(),
        endISO: new Date(o.endDate).toISOString()
      })),
      events: filteredEvents.map(e => ({ title: e.title, start: e.start.toISOString(), end: e.end.toISOString() }))
    });

    setWeekIosCalendarEvents(filteredEvents);
  }, [currentDate, availability?.iosOverrides]);

  // Note: Google Calendar events are now synced in real-time via backend Watch API
  // No polling needed - Firestore listener in BookingSystemContext handles updates
  // Events automatically update when backend receives webhook from Google

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Handle swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.changedTouches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    setTouchEnd(e.changedTouches[0].clientX);
  };

  // Process swipe detection
  useEffect(() => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50; // Swipe left (minimum 50px)
    const isRightSwipe = distance < -50; // Swipe right (minimum 50px)

    if (isLeftSwipe) {
      handleNextWeek();
    }
    if (isRightSwipe) {
      handlePrevWeek();
    }

    // Reset touch values
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd]);
  
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Start week on Monday
  const today = startOfToday();
  
  const handlePrevWeek = () => {
    setSlideDirection(-1);
    setCurrentDate(prev => subWeeks(prev, 1));
  };
  
  const handleNextWeek = () => {
    setSlideDirection(1);
    setCurrentDate(prev => addWeeks(prev, 1));
  };

  // Update the slide variants for horizontal movement
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction * 100,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction * -100,
      opacity: 0
    })
  };

  // Generate week days with dates
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i);
    return {
      dayName: format(date, 'EEE'),
      dayNumber: format(date, 'd'),
      date: date,
      isPast: isBefore(date, today),
    };
  });

  const isTimeSlotPast = (date: Date, hour: number, minute: number) => {
    const slotTime = setMinutes(setHours(date, hour), minute);
    return isBefore(slotTime, now);
  };

  // Get booking that starts at this exact time slot
  const getBookingStartingAt = (date: Date, hour: number, minute: number): Booking | null => {
    const slotTime = setMinutes(setHours(date, hour), minute).getTime();

    return bookings.find(booking => {
      const duration = (booking as any).estimatedDuration ?? (booking as any).estimatedTime;
      if (!booking.scheduledTimestamp?.toDate || !duration) {
        return false;
      }
      const bookingTime = booking.scheduledTimestamp.toDate().getTime();
      return Math.abs(bookingTime - slotTime) < 15 * 60 * 1000; // Within 15 minutes
    }) || null;
  };

  // Get Google Calendar event that starts at this exact time slot
  const getCalendarEventStartingAt = (date: Date, hour: number, minute: number): GoogleCalendarEvent | null => {
    if (weekCalendarEvents.length === 0) return null;

    const slotTime = setMinutes(setHours(date, hour), minute).getTime();

    return weekCalendarEvents.find(event => {
      const eventTime = event.start.getTime();
      return Math.abs(eventTime - slotTime) < 15 * 60 * 1000; // Within 15 minutes
    }) || null;
  };

  // Get iOS Calendar event that starts at this exact time slot
  const getIosCalendarEventStartingAt = (date: Date, hour: number, minute: number): IosCalendarEvent | null => {
    if (weekIosCalendarEvents.length === 0) return null;

    const slotTime = setMinutes(setHours(date, hour), minute).getTime();

    return weekIosCalendarEvents.find(event => {
      const eventTime = event.start.getTime();
      // For all-day or multi-day events, show at midnight (00:00)
      const eventStartAtMidnight = setMinutes(setHours(new Date(eventTime), 0), 0).getTime();
      
      // Show event if:
      // 1. It starts within 15 minutes of this slot OR
      // 2. It's an all-day event that spans this date and we're at the start of the day
      const isExactStart = Math.abs(eventTime - slotTime) < 15 * 60 * 1000;
      const isAllDayOnDate = eventStartAtMidnight !== eventTime && // Multi-hour event
        event.start.getTime() <= slotTime &&
        event.end.getTime() > slotTime &&
        hour === 0 && minute === 0; // Show at midnight
      
      return isExactStart || isAllDayOnDate;
    }) || null;
  };

  // Calculate booking height as number of grid rows it should span
  const getBookingRowSpan = (booking: Booking): number => {
    const duration = (booking as any).estimatedDuration ?? (booking as any).estimatedTime ?? 1;
    return duration * 4; // 4 slots per hour
  };

  // Calculate calendar event height as number of grid rows it should span
  const getCalendarEventRowSpan = (event: GoogleCalendarEvent): number => {
    const durationMs = event.end.getTime() - event.start.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);
    return Math.ceil(durationHours * 4); // 4 slots per hour
  };

  // Calculate iOS calendar event height as number of grid rows it should span
  const getIosCalendarEventRowSpan = (event: IosCalendarEvent): number => {
    const durationMs = event.end.getTime() - event.start.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);
    return Math.ceil(durationHours * 4); // 4 slots per hour
  };

  // Check if a time slot is available based on the weekly schedule and date overrides
  const isTimeSlotAvailable = (date: Date, hour: number, minute: number): boolean => {
    if (!availability) return false;
    
    const dateString = format(date, 'yyyy-MM-dd');
    const dayOfWeek = DAY_MAP[getDay(date)];
    
    // Get the day settings (monthSchedule takes highest precedence, then dateOverrides, then week schedule)
    let daySettings: DaySettings | undefined;
    if (availability.monthSchedule && availability.monthSchedule[dateString]) {
      daySettings = availability.monthSchedule[dateString];
    } else if (availability.dateOverrides && availability.dateOverrides[dateString]) {
      daySettings = availability.dateOverrides[dateString];
    } else if (availability.weekSchedule) {
      daySettings = availability.weekSchedule[dayOfWeek];
    }
    
    if (!daySettings || !daySettings.isEnabled) return false;
    
    // Convert time slot to minutes from midnight
    const slotStart = hour * 60 + minute;
    const slotEnd = slotStart + 15; // 15-minute slot
    
    // Check if this 15-minute slot overlaps with any available time slots
    return daySettings.timeSlots.some(slot => {
      // Check if there's any overlap between the 15-minute slot and the available time slot
      return slotStart < slot.end && slotEnd > slot.start;
    });
  };

  return (
    <div
      className="phone-container-padded flex flex-col h-[calc(100vh-var(--bottom-nav-height,80px))] px-2 pt-2 pb-4"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header section */}
      <div className="flex items-center justify-between flex-shrink-0">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={handlePrevWeek}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
        >
          <ChevronLeftIcon className="w-6 h-6" />
        </motion.button>
        
        <div className="flex flex-col items-center flex-1">
          <motion.h2 
            key={currentDate.toISOString()}
            custom={slideDirection}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="text-xl font-medium"
          >
            {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
          </motion.h2>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleNextWeek}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
        >
          <ChevronRightIcon className="w-6 h-6" />
        </motion.button>
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 pb-3 flex-shrink-0">
        <div className="flex items-center text-xs text-primary gap-2">
          <div className="w-2 h-2 bg-primary rounded-full"></div>
          <span>Bookings</span>
        </div>
        {googleCalendar.isConnected && googleCalendar.email && (
          <div className="flex items-center text-xs text-orange-700 dark:text-orange-400 gap-2">
            <div className="w-2 h-2 bg-orange-700 dark:bg-orange-400 rounded-full"></div>
            <span>Google Calendar</span>
          </div>
        )}
        {iosCalendar.isConnected && (
          <div className="flex items-center text-xs text-orange-700 dark:text-orange-400 gap-2">
            <div className="w-2 h-2 bg-orange-700 dark:bg-orange-400 rounded-full"></div>
            <span>iOS Calendar</span>
          </div>
        )}
        <div className="flex items-center text-xs text-muted-foreground gap-2">
          <div className="w-2 h-2 bg-border rounded-full"></div>
          <span>Availability</span>
        </div>
      </div>
      
      {/* Calendar Grid */}
      <div className="flex-1 min-h-0 flex flex-col border border-border rounded-lg overflow-hidden mb-4 bg-background">
        {/* Header Row */}
        <div className="grid grid-cols-[40px_repeat(7,1fr)] flex-shrink-0 bg-background border-b border-border">
          {/* Empty corner */}
          <div className="h-14 bg-background" />
          
          {/* Days header with horizontal animation */}
          {weekDays.map(({ dayName, dayNumber, date, isPast }) => (
            <div
              key={date.toISOString()}
              className={cn(
                "bg-background",
                isPast && "opacity-50"
              )}
            >
              <div className="h-14 flex flex-col items-center justify-center">
                <span className={cn(
                  "text-sm font-medium text-muted-foreground",
                  isPast && "opacity-60"
                )}>
                  {dayName}
                </span>
                <span className={cn(
                  "text-2xl text-foreground",
                  isPast && "opacity-60",
                  isSameDay(date, now) && "text-primary"
                )}>
                  {dayNumber}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Time Slots Grid */}
        <div className="flex-1 min-h-0">
          <div className="grid grid-cols-[40px_repeat(7,1fr)] h-full" style={{ gridTemplateRows: `repeat(${TIME_SLOTS.length}, 1fr)` }}>
          {/* Static time grid */}
          {TIME_SLOTS.map(({ hour, minute }, index) => {
            const isHourStart = minute === 0;
            
            return (
              <React.Fragment key={`slot-${hour}-${minute}`}>
                <div
                  className={cn(
                    "relative border-r border-border bg-background",
                    isHourStart && "border-t border-border"
                  )}
                >
                  {isHourStart && (
                    <span className="absolute top-0 right-0.5 text-[10px] text-muted-foreground leading-none">
                      {hour}
                    </span>
                  )}
                </div>
                
                {weekDays.map(({ date, isPast }, dayIndex) => {
                  const isAvailable = isTimeSlotAvailable(date, hour, minute);
                  const isPastSlot = isPast || isTimeSlotPast(date, hour, minute);
                  
                  // Get the index of current slot
                  const currentIndex = index;
                  
                  // Check if previous and next slots are also available
                  const isPrevSlotAvailable = currentIndex > 0 && 
                    isTimeSlotAvailable(
                      date, 
                      TIME_SLOTS[currentIndex - 1].hour, 
                      TIME_SLOTS[currentIndex - 1].minute
                    );
                  const isNextSlotAvailable = currentIndex < TIME_SLOTS.length - 1 && 
                    isTimeSlotAvailable(
                      date, 
                      TIME_SLOTS[currentIndex + 1].hour, 
                      TIME_SLOTS[currentIndex + 1].minute
                    );
                  
                  return (
                    <div
                      key={`slot-${date.toISOString()}-${hour}-${minute}`}
                      className={cn(
                        dayIndex < weekDays.length - 1 && "border-r border-border",
                        "relative bg-background",
                        isHourStart && "border-t border-border",
                        "transition-colors",
                        !isPastSlot && "hover:bg-muted/30"
                      )}
                    >
                      {/* Show availability */}
                      {isAvailable && (
                        <div className={cn(
                          "absolute bg-border",
                          "left-0.5 right-0.5",
                          // Top inset only if previous slot is not available
                          isPrevSlotAvailable ? "top-0" : "top-px",
                          // Bottom inset only if next slot is not available
                          isNextSlotAvailable ? "bottom-0" : "bottom-px",
                          // Round top corners if previous slot is not available
                          !isPrevSlotAvailable && "rounded-t-md",
                          // Round bottom corners if next slot is not available
                          !isNextSlotAvailable && "rounded-b-md"
                        )} />
                      )}
                      
                      {/* Show booking */}
                      {!isPastSlot && (() => {
                        const booking = getBookingStartingAt(date, hour, minute);
                        if (booking) {
                          const rowSpan = getBookingRowSpan(booking);
                          return (
                            <>
                              <div
                                className="absolute left-0.5 right-0.5 bg-primary rounded-md z-10 flex flex-col text-[7px] text-white p-1 cursor-pointer overflow-hidden"
                                style={{
                                  height: `calc(${rowSpan} * 100%)`,
                                }}
                                onClick={() => setSelectedBooking(booking)}
                              >
                                <div className="font-medium">Client Booking: {booking.clientInstagram || booking.clientEmail || booking.clientUsername}</div>
                                <div>{format(booking.scheduledTimestamp.toDate(), 'HH:mm')}</div>
                                <div>{booking.estimatedDuration}h • {booking.type}</div>
                              </div>

                              {/* Booking Details Dialog */}
                              {selectedBooking?.id === booking.id && (
                              <Dialog open={true} onOpenChange={() => setSelectedBooking(null)}>
                                <DialogPortal>
                                  <DialogPrimitive.Overlay
                                    className="fixed inset-0 z-[60] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                                  />
                                  <DialogPrimitive.Content
                                    className="fixed left-0 right-0 top-[50%] z-[60] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
                                  >
                                    {/* Header */}
                                    <div className="flex items-center justify-between p-6 pb-4">
                                      <h2 className="text-xl font-bold text-foreground flex items-center gap-3">
                                        <Bookmark className="w-6 h-6 flex-shrink-0" />
                                        Booking Details:
                                      </h2>
                                      <DialogPrimitive.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-0 disabled:pointer-events-none">
                                        <X className="h-5 w-5" />
                                        <span className="sr-only">Close</span>
                                      </DialogPrimitive.Close>
                                    </div>

                                    {/* Content */}
                                    <div className="px-6 pb-6 space-y-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                                      {/* Booking Details Section */}
                                      <div className="space-y-3">
                                        <div>
                                          <label className="block text-xs font-medium text-muted-foreground mb-1 flex items-center gap-2">
                                            <Calendar className="w-4 h-4" />
                                            Date & Time
                                          </label>
                                          <p className="text-sm text-foreground">
                                            {format(selectedBooking.scheduledTimestamp.toDate(), 'EEEE, MMMM d, yyyy')}
                                          </p>
                                          <p className="text-sm text-foreground">
                                            {format(selectedBooking.scheduledTimestamp.toDate(), 'HH:mm')} - {format(new Date(selectedBooking.scheduledTimestamp.toDate().getTime() + selectedBooking.estimatedDuration * 60 * 60 * 1000), 'HH:mm')}
                                          </p>
                                        </div>

                                        <div>
                                          <label className="block text-xs font-medium text-muted-foreground mb-1 flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            Duration
                                          </label>
                                          <p className="text-sm text-foreground">
                                            {selectedBooking.estimatedDuration}h
                                          </p>
                                        </div>

                                        {selectedBooking.shortSummary && (
                                          <div>
                                            <label className="block text-xs font-medium text-muted-foreground mb-1">
                                              Description
                                            </label>
                                            <p className="text-sm text-foreground">
                                              {selectedBooking.shortSummary}
                                            </p>
                                          </div>
                                        )}
                                      </div>

                                      {/* Client Information Section */}
                                      <div>
                                        <h3 className="text-xl font-bold text-foreground flex items-center gap-3 mb-4">
                                          <User className="w-6 h-6 flex-shrink-0" />
                                          Client Information:
                                        </h3>
                                        <div className="space-y-3">
                                          {/* Email */}
                                          <div>
                                            <label className="block text-xs font-medium text-muted-foreground mb-1 flex items-center gap-2">
                                              <Mail className="w-4 h-4" />
                                              Email
                                            </label>
                                            <p className="text-sm text-foreground">
                                              {selectedBooking.clientEmail || 'Not provided'}
                                            </p>
                                          </div>
                                          
                                          {/* Instagram */}
                                          <div>
                                            <label className="block text-xs font-medium text-muted-foreground mb-1 flex items-center gap-2">
                                              <Instagram className="w-4 h-4" />
                                              Instagram
                                            </label>
                                            <p className="text-sm text-foreground">
                                              {selectedBooking.clientInstagram || 'Not provided'}
                                            </p>
                                          </div>
                                          
                                          {/* Phone Number */}
                                          <div>
                                            <label className="block text-xs font-medium text-muted-foreground mb-1 flex items-center gap-2">
                                              <Phone className="w-4 h-4" />
                                              Phone Number
                                            </label>
                                            <p className="text-sm text-foreground">
                                              {selectedBooking.clientPhoneNumber || 'Not provided'}
                                            </p>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Chat Button */}
                                      <div className="flex justify-center pt-4">
                                        <Button
                                          className="px-6 py-2 text-sm font-medium"
                                          onClick={() => {
                                            setSelectedBooking(null);
                                            navigate(`/chats/${selectedBooking.chatId}`);
                                          }}
                                        >
                                          <div className="flex items-center gap-2">
                                            <MessageCircle className="w-4 h-4" />
                                            <span>Open Chat</span>
                                          </div>
                                        </Button>
                                      </div>
                                    </div>
                                  </DialogPrimitive.Content>
                                </DialogPortal>
                              </Dialog>
                              )}
                            </>
                          );
                        }

                        // Show Google Calendar event
                        const calendarEvent = getCalendarEventStartingAt(date, hour, minute);
                        if (calendarEvent) {
                          const rowSpan = getCalendarEventRowSpan(calendarEvent);
                          return (
                            <>
                              <div
                                className="absolute left-0.5 right-0.5 bg-orange-700 rounded-md z-10 flex flex-col text-[11px] text-white p-1.5 cursor-pointer overflow-hidden"
                                style={{
                                  height: `calc(${rowSpan} * 100%)`,
                                }}
                                onClick={() => setSelectedCalendarEvent(calendarEvent)}
                              >
                                <div className="font-medium">{calendarEvent.summary}</div>
                                <div>{format(calendarEvent.start, 'HH:mm')}-{format(calendarEvent.end, 'HH:mm')}</div>
                              </div>

                              {/* Calendar Event Details Dialog */}
                              {selectedCalendarEvent?.id === calendarEvent.id && (
                                <Dialog open={true} onOpenChange={() => setSelectedCalendarEvent(null)}>
                                  <DialogPortal>
                                    <DialogPrimitive.Overlay
                                      className="fixed inset-0 z-[60] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                                    />
                                    <DialogPrimitive.Content
                                      className="fixed left-0 right-0 top-[50%] z-[60] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
                                    >
                                      {/* Header */}
                                      <div className="flex items-center justify-between p-6 pb-4">
                                        <div className="text-lg font-semibold">
                                          {selectedCalendarEvent.summary}
                                        </div>
                                        <DialogPrimitive.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-0 disabled:pointer-events-none">
                                          <X className="h-5 w-5" />
                                          <span className="sr-only">Close</span>
                                        </DialogPrimitive.Close>
                                      </div>

                                      {/* Content */}
                                      <div className="px-6 pb-6 space-y-4">
                                        
                                        <div className="text-base">
                                          <div>{format(selectedCalendarEvent.start, 'EEEE, MMMM d, yyyy')}</div>
                                          <div>{format(selectedCalendarEvent.start, 'HH:mm')} - {format(selectedCalendarEvent.end, 'HH:mm')} ({Math.round((selectedCalendarEvent.end.getTime() - selectedCalendarEvent.start.getTime()) / (1000 * 60 * 60) * 10) / 10}h)</div>
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                          Source: Google Calendar
                                        </div>
                                      </div>
                                    </DialogPrimitive.Content>
                                  </DialogPortal>
                                </Dialog>
                              )}
                            </>
                          );
                        }

                        // Show iOS Calendar event
                        const iosCalendarEvent = getIosCalendarEventStartingAt(date, hour, minute);
                        if (iosCalendarEvent) {
                          // For all-day or multi-day events, show as a banner at the top
                          const durationMs = iosCalendarEvent.end.getTime() - iosCalendarEvent.start.getTime();
                          const isAllDayEvent = durationMs >= 24 * 60 * 60 * 1000; // >= 1 day
                          
                          const rowSpan = isAllDayEvent ? 1 : getIosCalendarEventRowSpan(iosCalendarEvent);
                          
                          return (
                            <>
                              <div
                                className={cn(
                                  "absolute left-0.5 right-0.5 bg-orange-700 rounded-md z-10 flex flex-col text-[11px] text-white p-1.5 cursor-pointer overflow-hidden",
                                  isAllDayEvent && "bg-orange-600/80 border-l-2 border-orange-800"
                                )}
                                style={{
                                  height: isAllDayEvent ? 'auto' : `calc(${rowSpan} * 100%)`,
                                  minHeight: isAllDayEvent ? '20px' : undefined,
                                }}
                                onClick={() => setSelectedIosCalendarEvent(iosCalendarEvent)}
                              >
                                <div className="font-medium truncate">{iosCalendarEvent.title}</div>
                                {!isAllDayEvent && (
                                  <div className="text-[10px]">{format(iosCalendarEvent.start, 'HH:mm')}-{format(iosCalendarEvent.end, 'HH:mm')}</div>
                                )}
                              </div>

                              {/* iOS Calendar Event Details Dialog */}
                              {selectedIosCalendarEvent?.id === iosCalendarEvent.id && (
                                <Dialog open={true} onOpenChange={() => setSelectedIosCalendarEvent(null)}>
                                  <DialogPortal>
                                    <DialogPrimitive.Overlay
                                      className="fixed inset-0 z-[60] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                                    />
                                    <DialogPrimitive.Content
                                      className="fixed left-0 right-0 top-[50%] z-[60] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
                                    >
                                      {/* Header */}
                                      <div className="flex items-center justify-between p-6 pb-4">
                                        <div className="text-lg font-semibold">
                                          {selectedIosCalendarEvent.title}
                                        </div>
                                        <DialogPrimitive.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-0 disabled:pointer-events-none">
                                          <X className="h-5 w-5" />
                                          <span className="sr-only">Close</span>
                                        </DialogPrimitive.Close>
                                      </div>

                                      {/* Content */}
                                      <div className="px-6 pb-6 space-y-4">
                                        <div className="text-base">
                                          <div>{format(selectedIosCalendarEvent.start, 'EEEE, MMMM d, yyyy')}</div>
                                          <div>{format(selectedIosCalendarEvent.start, 'HH:mm')} - {format(selectedIosCalendarEvent.end, 'HH:mm')} ({Math.round((selectedIosCalendarEvent.end.getTime() - selectedIosCalendarEvent.start.getTime()) / (1000 * 60 * 60) * 10) / 10}h)</div>
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                          Source: iOS Calendar
                                        </div>
                                      </div>
                                    </DialogPrimitive.Content>
                                  </DialogPortal>
                                </Dialog>
                              )}
                            </>
                          );
                        }

                        return null;
                      })()}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingSystemCalendar;
